/**
 * Original exam pictures (SVG drawings) in the players and the reviews: a picture on a light
 * « paper » card (the drawings carry their own light background, readable in both themes), a
 * zoom in an accessible full-screen dialog, the picture options (TEF CO section 1, Goethe A2
 * Hören Teil 3) as radio cards, the Goethe pool of pictures to match and the IELTS plan with its
 * text description. The pure helpers (alt, link, label) are shared with the phone in
 * @agencyos/core. When the link is missing or fails, the alt text is shown instead, so that the
 * item stays answerable. Links are signed and short-lived: never stored.
 */

import { type ExamPicture, hasPictures, optionPictureLabel, parsePicture, parsePictures, pictureAlt, pictureSource } from "@agencyos/core";
import { useModal } from "@agencyos/ui-web";
import { Check, ImageOff, X, ZoomIn } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import "./pictures.css";

/** The drawing itself, or its description in a bordered box when the link is missing or fails. */
function PictureImage({ picture, alt }: { picture: ExamPicture; alt: string }) {
  const { t } = useTranslation();
  const src = pictureSource(picture, "web");
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return (
      <span className="ac-picture__fallback" role="img" aria-label={alt}>
        <ImageOff aria-hidden size={18} />
        <span aria-hidden="true">
          <span className="ac-picture__fallback-title">{t("academy.picture.unavailable")}</span> {alt}
        </span>
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      width={picture.width || undefined}
      height={picture.height || undefined}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

/** Full-screen zoom: role=dialog, focus inside, Escape and the close button close it, focus returns. */
export function PictureZoom({ picture, alt, onClose }: { picture: ExamPicture; alt: string; onClose: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModal(true, onClose, ref);
  const src = pictureSource(picture, "web");
  return createPortal(
    <div className="ac-zoom">
      <div className="ac-zoom__scrim" aria-hidden="true" onClick={onClose} />
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="ac-zoom__panel">
        <div className="ac-zoom__bar">
          <h2 id={titleId} className="ac-zoom__title">
            {t("academy.picture.dialog")}
          </h2>
          <button type="button" className="ac-picture__zoom" data-autofocus onClick={onClose}>
            <X aria-hidden size={16} />
            {t("academy.picture.close")}
          </button>
        </div>
        <div className="ac-zoom__paper">
          {src ? (
            <img src={src} alt={alt} width={picture.width || undefined} height={picture.height || undefined} decoding="async" />
          ) : (
            <p className="ac-picture__fallback">{alt}</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ZoomButton({ onClick, label }: { onClick: () => void; label: string }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="ac-picture__zoom" onClick={onClick} aria-label={label}>
      <ZoomIn aria-hidden size={16} />
      <span aria-hidden="true">{t("academy.picture.zoomShort")}</span>
    </button>
  );
}

export interface ExamPictureViewProps {
  picture: ExamPicture | null;
  /** Language of the alt text (the exam's language for the TCF and the TEF); interface language by default. */
  lang?: string;
  size?: "large" | "medium" | "small";
  /** Alt text when the picture has none. */
  fallbackAlt?: string;
  caption?: ReactNode;
  className?: string;
}

/** A picture of a question or of a part, on a paper card, opened large on click or with « Agrandir ». */
export function ExamPictureView({ picture, lang, size = "large", fallbackAlt = "", caption, className = "" }: ExamPictureViewProps) {
  const { t, i18n } = useTranslation();
  const [zoom, setZoom] = useState(false);
  if (!picture) return null;
  const alt = pictureAlt(picture, lang ?? i18n.language) || fallbackAlt;
  const zoomable = Boolean(pictureSource(picture, "web"));
  return (
    <figure className={`ac-picture ac-picture--${size} ${className}`.trim()}>
      <div className="ac-picture__frame">
        {zoomable ? (
          <button type="button" className="ac-picture__open" onClick={() => setZoom(true)} title={t("academy.picture.zoom")}>
            <PictureImage picture={picture} alt={alt} />
          </button>
        ) : (
          <PictureImage picture={picture} alt={alt} />
        )}
        {zoomable && <ZoomButton onClick={() => setZoom(true)} label={t("academy.picture.zoom")} />}
      </div>
      {caption ? <figcaption className="ac-picture__caption">{caption}</figcaption> : null}
      {zoom && <PictureZoom picture={picture} alt={alt} onClose={() => setZoom(false)} />}
    </figure>
  );
}

export interface PictureOptionsProps {
  /** Name of the radio group (one per question). */
  name: string;
  options: string[];
  images: (ExamPicture | null)[];
  /** Label under each picture (default: the option before « : », « Dessin A », « Bild a »). */
  labels?: string[];
  /** Answering: the choice and its change (radio cards). Without `onChange`: a read-only list. */
  value?: number | null;
  onChange?: (index: number) => void;
  disabled?: boolean;
  /** Correction (training, review): the right option and the one given. */
  answer?: number | null;
  given?: number | null;
  /** Review: the full option texts under the pictures. */
  showText?: boolean;
  lang?: string;
  legend?: string;
}

/** The options as pictures: a 2×2 grid (4 options) or 3 columns (3 options), radio semantics. */
export function PictureOptions({
  name,
  options,
  images,
  labels,
  value = null,
  onChange,
  disabled = false,
  answer = null,
  given = null,
  showText = false,
  lang,
  legend,
}: PictureOptionsProps) {
  const { t, i18n } = useTranslation();
  const [zoom, setZoom] = useState<number | null>(null);
  const altLang = lang ?? i18n.language;
  const columns = options.length === 3 || options.length > 4 ? 3 : 2;
  const zoomed = zoom === null ? null : (images[zoom] ?? null);
  const corrected = answer !== null && answer >= 0;
  return (
    <div
      role={onChange ? "radiogroup" : "group"}
      aria-label={legend ?? t("academy.picture.choose")}
      aria-disabled={onChange && disabled ? true : undefined}
      className="ac-picopts-wrap"
    >
      <ul className={`ac-picopts ac-picopts--${columns}`}>
        {options.map((option, index) => {
          const picture = images[index] ?? null;
          const label = labels?.[index] ?? optionPictureLabel(option);
          const alt = pictureAlt(picture, altLang) || option;
          const right = corrected && index === answer;
          const wrong = corrected && !right && given !== null && index === given;
          const on = Boolean(onChange) && value === index;
          const className = `ac-picopt${on ? " ac-picopt--on" : ""}${right ? " ac-picopt--right" : ""}${wrong ? " ac-picopt--wrong" : ""}`;
          const body = (
            <>
              <span className="ac-picopt__paper">
                {picture ? <PictureImage picture={picture} alt={alt} /> : <span className="ac-picture__fallback">{option}</span>}
              </span>
              <span className="ac-picopt__label">
                {(on || right) && <Check aria-hidden size={15} />}
                {label}
              </span>
              {showText && option !== label && <span className="ac-picopt__text">{option}</span>}
              {right && <span className="visually-hidden">{t("academy.picture.right")}</span>}
              {!onChange && given === index && <span className="ac-picopt__text ac-picopt__given">{t("academy.picture.given")}</span>}
            </>
          );
          return (
            <li key={index} className={className}>
              {onChange ? (
                <label className="ac-picopt__card">
                  <input type="radio" name={name} value={index} checked={value === index} disabled={disabled} onChange={() => onChange(index)} />
                  {body}
                </label>
              ) : (
                <div className="ac-picopt__card">{body}</div>
              )}
              {picture && pictureSource(picture, "web") && (
                <span className="ac-picopt__zoom">
                  <ZoomButton onClick={() => setZoom(index)} label={`${t("academy.picture.zoom")} : ${label}`} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {zoomed && zoom !== null && (
        <PictureZoom picture={zoomed} alt={pictureAlt(zoomed, altLang) || (options[zoom] ?? "")} onClose={() => setZoom(null)} />
      )}
    </div>
  );
}

export interface PoolPicture {
  key: string;
  text: string;
  image?: ExamPicture | null;
}

/** A pool shows pictures when at least one of its entries has one. */
export function poolHasPictures(items: readonly PoolPicture[]): boolean {
  return items.some((item) => Boolean(item.image));
}

/** Goethe A2 Hören Teil 2: the pictures a–i to match, 3 columns, the letter under each; the text
 *  stays for screen readers (and as the alt when the picture has none). */
export function PicturePool({ items, lang, label }: { items: PoolPicture[]; lang?: string; label?: string }) {
  const { t, i18n } = useTranslation();
  const [zoom, setZoom] = useState<number | null>(null);
  const altLang = lang ?? i18n.language;
  const zoomed = zoom === null ? null : (items[zoom]?.image ?? null);
  return (
    <div className="ac-picpool-wrap">
      <ul className="ac-picopts ac-picopts--3 ac-picpool" aria-label={label ?? t("academy.picture.pool")}>
        {items.map((item, index) => {
          const alt = pictureAlt(item.image ?? null, altLang) || item.text;
          return (
            <li key={item.key} className="ac-picopt">
              <div className="ac-picopt__card">
                <span className="ac-picopt__paper">
                  {item.image ? <PictureImage picture={item.image} alt={alt} /> : <span className="ac-picture__fallback">{item.text}</span>}
                </span>
                <span className="ac-picopt__label">
                  {item.key}
                  <span className="visually-hidden">: {item.text}</span>
                </span>
              </div>
              {item.image && pictureSource(item.image, "web") && (
                <span className="ac-picopt__zoom">
                  <ZoomButton onClick={() => setZoom(index)} label={`${t("academy.picture.zoom")} : ${item.key}`} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {zoomed && zoom !== null && (
        <PictureZoom picture={zoomed} alt={pictureAlt(zoomed, altLang) || (items[zoom]?.text ?? "")} onClose={() => setZoom(null)} />
      )}
    </div>
  );
}

/** IELTS plan / map: the picture above the questions, the text plan kept in a collapsible block
 *  (the accessible alternative). Without a picture, only the text plan is shown, as before. */
export function PlanPicture({ picture, lang, children }: { picture: ExamPicture | null; lang?: string; children?: ReactNode }) {
  const { t } = useTranslation();
  if (!picture) return <>{children}</>;
  return (
    <div className="ac-picplan">
      <ExamPictureView picture={picture} lang={lang} />
      {children ? (
        <details className="ac-picplan__text">
          <summary>{t("academy.picture.planText")}</summary>
          <div className="ac-picplan__body">{children}</div>
        </details>
      ) : null}
    </div>
  );
}

/** Back office: a small preview of the pictures of a question (read-only `pictures` of the API). */
export function PicturesPreview({ pictures, options, lang = "fr" }: { pictures: unknown; options: string[]; lang?: string }) {
  const data = typeof pictures === "object" && pictures !== null ? (pictures as Record<string, unknown>) : {};
  const image = parsePicture(data.image);
  const images = parsePictures(data.option_images);
  if (!image && !hasPictures(images)) return null;
  return (
    <div className="ac-picpreview">
      <ExamPictureView picture={image} lang={lang} size="small" />
      {hasPictures(images) && <PictureOptions name="picture-preview" options={options} images={images} lang={lang} />}
    </div>
  );
}
